/**
 * CASCATA DO CADASTRO DE PROJETO: CLIENTE → ORDEM DE SERVIÇO → PROJETO.
 *
 * Regras puras da interface, separadas da página para poderem ser exercitadas
 * sem DOM. Elas apenas ESTREITAM o que é oferecido; quem decide se a
 * combinação é aceitável continua sendo o serviço — a tela filtrada não
 * substitui a validação.
 */

export const NO_WORK_ORDERS_FOR_CLIENT_MESSAGE =
  "Nenhuma Ordem de Serviço cadastrada para este Cliente.";
export const WORK_ORDER_CLIENT_MISMATCH_MESSAGE =
  "A Ordem de Serviço selecionada não pertence ao Cliente informado.";
export const SELECT_CLIENT_FIRST_PLACEHOLDER = "Selecione o Cliente primeiro";

export interface ClientLike {
  id: string;
  name?: string | null;
  legalName?: string | null;
  status?: string | null;
  deletedAt?: string | null;
}

export interface WorkOrderOptionLike {
  id: string;
  code: string;
  clientId?: string | null;
  description?: string | null;
  status?: string | null;
}

/** Nome apresentável do Cliente, sem inventar um quando não há. */
export function formatClientLabel(client: ClientLike): string {
  return String(client.name || client.legalName || client.id);
}

/**
 * Clientes oferecidos para um novo Projeto: só os ativos e não excluídos.
 * A lista chega já restrita aos Clientes autorizados do usuário — aqui só se
 * remove quem não está apto a receber Projeto novo.
 */
export function clientOptionsForNewProject(clients: readonly ClientLike[]): ClientLike[] {
  return clients
    .filter(client => !client.deletedAt)
    .filter(client => {
      const status = String(client.status ?? "ACTIVE").toUpperCase();
      return status === "ACTIVE";
    })
    .sort((a, b) => formatClientLabel(a).localeCompare(formatClientLabel(b), "pt-BR", { numeric: true }));
}

/** Ordens de Serviço oferecidas depois de escolher o Cliente — só as dele. */
export function workOrderOptionsForClient(
  clientId: string,
  workOrders: readonly WorkOrderOptionLike[]
): WorkOrderOptionLike[] {
  if (!clientId) return [];
  return workOrders
    .filter(wo => wo.clientId === clientId)
    .sort((a, b) => a.code.localeCompare(b.code, "pt-BR", { numeric: true }));
}

/**
 * Trocar o Cliente invalida a OS que não pertença ao novo Cliente.
 * Devolve a OS que pode permanecer, ou vazio.
 */
export function keepWorkOrderOnClientChange(
  currentWorkOrderId: string,
  availableWorkOrders: readonly WorkOrderOptionLike[]
): string {
  if (!currentWorkOrderId) return "";
  return availableWorkOrders.some(wo => wo.id === currentWorkOrderId) ? currentWorkOrderId : "";
}

/** A OS informada é mesmo daquele Cliente? */
export function workOrderBelongsToClient(
  workOrder: WorkOrderOptionLike | null | undefined,
  clientId: string
): boolean {
  if (!workOrder || !clientId) return false;
  return workOrder.clientId === clientId;
}

/** Rótulo da OS na lista: código e, quando há, a descrição. */
export function formatWorkOrderOptionLabel(workOrder: WorkOrderOptionLike): string {
  return workOrder.description ? `${workOrder.code} — ${workOrder.description}` : workOrder.code;
}

/**
 * Restringe as OS carregadas aos Clientes que o usuário pode ver.
 *
 * A tela precisa das OS de vários Clientes para montar a cascata e a árvore,
 * e não pode passar a mostrar OS de um Cliente ao qual o usuário não tem
 * acesso só porque foram carregadas de uma vez.
 */
export function restrictWorkOrdersToClients(
  workOrders: readonly WorkOrderOptionLike[],
  clients: readonly ClientLike[]
): WorkOrderOptionLike[] {
  const allowed = new Set(clients.map(client => client.id));
  return workOrders.filter(wo => !!wo.clientId && allowed.has(wo.clientId));
}

/* ══════════════════════════════════════════════════════════════════════
   OS × PROJETO — a regra oficial de quais OS pertencem a um Projeto
   ══════════════════════════════════════════════════════════════════════ */

/** Um vínculo OS ↔ Projeto (`WorkOrderProjectLink`), a fonte real da verdade. */
export interface WorkOrderProjectLinkLike {
  workOrderId: string;
  projectId: string;
}

/**
 * AS OS QUE DEVEM APARECER QUANDO UM PROJETO ESTÁ SELECIONADO.
 *
 * ────────────────────────────────────────────────────────────────────
 * Defeito corrigido: "Nenhuma OS cadastrada para este contexto".
 * ────────────────────────────────────────────────────────────────────
 *
 * A consulta antiga era `workOrders.filter(wo => wo.projectId === projectId)`.
 * Isso contraria a regra oficial da seção 23 do CLAUDE.md por dois motivos:
 *
 *   1. `WorkOrder.projectId` é campo LEGADO de vínculo único. A fonte real
 *      são os registros de `WorkOrderProjectLink` — uma OS pode estar
 *      vinculada a vários Projetos, e nenhum deles aparecia.
 *
 *   2. OS NÃO DEPENDE DE PROJETO PARA EXISTIR. Cliente é o único vínculo
 *      obrigatório, e `projectId = null` é o estado normal de uma OS
 *      recém-criada. O filtro excluía exatamente essas — que são a maioria.
 *      O usuário cadastrava a OS, escolhia o Projeto no relatório, e a OS
 *      desaparecia.
 *
 * A regra correta devolve, dentre as OS JÁ RESTRITAS AO CLIENTE:
 *
 *   · as vinculadas ao Projeto (por `WorkOrderProjectLink` ou pelo legado);
 *   · as AINDA NÃO vinculadas a Projeto nenhum.
 *
 * As não vinculadas continuam aparecendo porque, do contrário, não haveria
 * como escolhê-las — e vincular Projeto a uma OS é um passo posterior e
 * opcional. Uma OS de OUTRO Projeto, essa sim, fica de fora.
 */
export function workOrdersForProjectContext<T extends { id: string; projectId?: string | null }>(
  workOrders: readonly T[],
  projectId: string | null | undefined,
  links: readonly WorkOrderProjectLinkLike[] = []
): T[] {
  const target = (projectId ?? "").trim();
  if (!target) return [...workOrders];

  const linkedToTarget = new Set(links.filter(l => String(l.projectId) === target).map(l => String(l.workOrderId)));
  const linkedToAny = new Set(links.map(l => String(l.workOrderId)));

  return workOrders.filter(wo => {
    const legacy = String(wo.projectId ?? "").trim();
    if (legacy === target) return true;
    if (linkedToTarget.has(String(wo.id))) return true;
    // Sem vínculo nenhum: continua disponível para ser escolhida.
    return legacy === "" && !linkedToAny.has(String(wo.id));
  });
}
